import { Box, TextField, Tooltip, Typography } from '@mui/material';
import { ISpecsHistorySchema } from '../../../@types/specsHistory';

type Props = {
  handleOpen: any;
  data: ISpecsHistorySchema;
};

const SpecsHistoryBox = ({ handleOpen, data }: Props) => {
  return (
    <Box
      p={3}
      mt={2}
      sx={{
        borderRadius: '15px',
        border: '1px solid #6373813b',
        position: 'relative',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Specs History/ Lens Meter
      </Typography>
      {/*
        Condition / Notes / Specs Type share one row inside a 50%-wide card, so
        each needs its own flex basis with minWidth: 0 (otherwise long notes
        push the other two out of view). They stack on small screens and long
        notes are clamped to two lines with the full text in a tooltip.
      */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          columnGap: 2,
          rowGap: 1,
          width: '100%',
        }}
      >
        <Typography
          sx={{
            color: 'text.secondary',
            flex: { xs: '1 1 auto', sm: '1 1 25%' },
            minWidth: 0,
          }}
        >
          <strong>Condition:</strong> {data.condition}
        </Typography>
        <Tooltip title={data.notes || ''} placement="top">
          <Typography
            sx={{
              color: 'text.secondary',
              flex: { xs: '1 1 auto', sm: '2 1 40%' },
              minWidth: 0,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            <strong>Notes:</strong> {data.notes}
          </Typography>
        </Tooltip>
        <Typography
          sx={{
            color: 'text.secondary',
            flex: { xs: '1 1 auto', sm: '1 1 25%' },
            minWidth: 0,
          }}
        >
          <strong>Specs Type:</strong> {data.specifications}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          mt: 5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          mt={1}
        >
          <Typography>Right</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              Sph
              <TextField
                disabled
                sx={{ width: '70px' }}
                value={
                  data?.rightSphericalPower
                    ? parseFloat(data.rightSphericalPower) >= 0
                      ? `+${parseFloat(data.rightSphericalPower).toFixed(2)}`
                      : parseFloat(data.rightSphericalPower).toFixed(2)
                    : ''
                }
                name="specificationHistories.rightSphericalPower"
                type="string"
                inputProps={{
                  step: 0.25,
                  min: -10,
                  max: 10,
                  onChange: (e: any) => {
                    let value = parseFloat(e.target.value).toFixed(2);
                    if (parseFloat(value) > 0) {
                      value = `+${value}`;
                    }
                    e.target.value = value;
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                width: '1px',
                margin: '0 10px',
                height: '40px',
                background: 'black',
                position: 'relative',
                top: '30px',
                transform: 'rotate(14deg)',
              }}
            ></Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              Cyl
              <TextField
                type="number"
                disabled
                sx={{ width: '90px' }}
                value={parseFloat(data?.rightCylinder).toFixed(2)}
                name="specificationHistories.rightCylinder"
              />
            </Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              Axis
              <TextField
                type="number"
                sx={{ width: '90px' }}
                disabled
                value={parseFloat(data?.rightAxis).toFixed(2)}
                name="specificationHistories.rightAxis"
              />
            </Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              Dist PD
              <TextField
                type="number"
                disabled
                sx={{ width: '90px' }}
                value={parseFloat(data?.rightDistancePD).toFixed(2)}
                name="specificationHistories.rightDistancePD"
              />
            </Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              BVD
              <TextField
                type="number"
                disabled
                sx={{ width: '90px' }}
                value={parseFloat(data?.rightBVD).toFixed(2)}
                name="specificationHistories.rightBVD"
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          mt={1}
        >
          <Typography>Left</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <TextField
                disabled
                sx={{ width: '70px' }}
                value={
                  data?.leftSphericalPower
                    ? parseFloat(data.leftSphericalPower) >= 0
                      ? `+${parseFloat(data.leftSphericalPower).toFixed(2)}`
                      : parseFloat(data.leftSphericalPower).toFixed(2)
                    : ''
                }
                name="specificationHistories.leftSphericalPower"
                type="string"
                inputProps={{
                  step: 0.25,
                  min: -10,
                  max: 10,
                  onChange: (e: any) => {
                    let value = parseFloat(e.target.value).toFixed(2);
                    if (parseFloat(value) > 0) {
                      value = `+${value}`;
                    }
                    e.target.value = value;
                  },
                }}
              />
            </Box>
            <Box
              className="line1"
              sx={{
                width: '1px',
                margin: '0 10px',
                height: '40px',
                background: 'black',
                position: 'relative',
                top: '9px',
                transform: 'rotate(14deg)',
              }}
            ></Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                disabled
                sx={{ width: '90px' }}
                value={data?.leftCylinder}
                name="specificationHistories.leftCylinder"
                inputProps={{
                  step: 0.25,
                  onChange: (e: any) => {
                    const value = parseFloat(e.target.value).toFixed(2);
                    e.target.value = value;
                  },
                }}
              />
            </Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                sx={{ width: '90px' }}
                disabled
                value={parseFloat(data?.leftAxis).toFixed(2)}
                name="specificationHistories.leftAxis"
                inputProps={{
                  step: 1,
                  min: 1,
                  max: 180,
                  onInput: (e: any) => {
                    if (e.target.value.length > 3) {
                      e.target.value = e.target.value.slice(0, 3);
                    }
                  },
                }}
              />
            </Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                sx={{ width: '90px' }}
                disabled
                value={parseFloat(data?.leftDistancePD).toFixed(2)}
                name="specificationHistories.leftDistancePD"
              />
            </Box>
            <Box
              mr={1}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <TextField
                type="number"
                disabled
                sx={{ width: '90px' }}
                value={parseFloat(data?.leftBVD).toFixed(2)}
                name="specificationHistories.leftBVD"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SpecsHistoryBox;
